import React from 'react';
import styled, { keyframes } from 'styled-components';
import arriving from './arriving.svg';
import future from './future.svg';
import failed from './failed.svg';
import active from './active.svg';
import completed from './completed.svg';
import radarIcon from './radar.png';
import { useTranslation } from 'react-i18next';

type StatusProps = {
    className?: string;
    orderStatus: OrderStatus;
    message?: string | null;
    isPartial?: boolean;
};

export enum OrderStatus {
    ORDER_STATUS_UNSPECIFIED = 0,
    ROUTING = 1,
    PENDING = 2,
    CONFIRMED = 3,
    CANCELLED = 4,
    WAITING = 5,
    DRIVING = 6,
    COMPLETED = 7,
    STATUS_CARE_REQ = 8,
    REJECTED = 9,
    ASSIGNED = 10,
    RESERVED = 11,
    UNRESERVED = 12,
}

export const PartialTrackingStatusesSupported = [
    OrderStatus.RESERVED,
    OrderStatus.PENDING,
    OrderStatus.ASSIGNED,
    OrderStatus.COMPLETED,
];

const StatusContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px 24px;
    box-shadow: 0 4px 8px -2px rgba(38, 50, 56, 0.12), 0 1px 1px 0 rgba(38, 50, 56, 0.04);
`;

const StatusTextContainer = styled.div`
    font-size: 16px;
    font-family: 'Graphik LCG', sans-serif;
    color: #263238;
`;

const StatusWithPartialTextContainer = styled.div`
    font-size: 14px;
    font-family: 'Graphik LCG', sans-serif;
    color: #263238;
    display: flex;
    flex-direction: column;
    padding: 2px 5px 4px 4px;
`;

const PartialTextContainer = styled.div`
    font-size: 14px;
    color: #6e848f;
`;

const StatusWithIconContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-left: 3px;
`;

export const Status = (props: StatusProps) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { t, i18n } = useTranslation('status');
    if (props === null) return null;

    const icon = getStatusIcon(props.orderStatus, !!props.isPartial);

    return (
        <StatusContainer className={props.className + ' Status'}>
            <StatusWithIconContainer>
                {icon}
                <StatusWithPartialTextContainer>
                    <StatusTextContainer>{props.message}</StatusTextContainer>
                    {props.isPartial && <PartialTextContainer>{t('Untrackable_ride')}</PartialTextContainer>}
                </StatusWithPartialTextContainer>
            </StatusWithIconContainer>
        </StatusContainer>
    );
};

type StatusIconProps = {
    pic?: string;
};

const StatusIconContainer = styled.div`
    padding: 0 10px 0 0;
    width: 24px;
    margin-right: 10px;
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
}
`;

const Spinner = styled.img`
    animation: ${spin} 0.7s linear infinite;
    transform-origin: 50% 50%;
    width: 16px;
    margin-right: 5px;
`;

const getStatusIcon = (status: OrderStatus, isPartial: boolean) => {
    if (isPartial && !PartialTrackingStatusesSupported.includes(status)) {
        // For partial tracking fleets always showing Active ride
        return <StatusIcon pic={active} />;
    }
    switch (status) {
        case OrderStatus.PENDING:
        case OrderStatus.RESERVED:
        case OrderStatus.ASSIGNED:
            return <StatusIcon pic={future} />;
        case OrderStatus.CONFIRMED:
            return <StatusIcon pic={arriving} />;
        case OrderStatus.CANCELLED:
        case OrderStatus.REJECTED:
            return <StatusIcon pic={failed} />;
        case OrderStatus.COMPLETED:
            return <StatusIcon pic={completed} />;
        case OrderStatus.ROUTING:
            return <Spinner src={radarIcon} alt='radar' width={16} height={16} />;
        default:
            return <StatusIcon pic={active} />;
    }
};

const StatusIcon = (icon: StatusIconProps) => {
    return (
        <StatusIconContainer className='status-icon'>
            <img src={icon.pic} alt='Icon' style={{ width: '24px' }} />
        </StatusIconContainer>
    );
};
